import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RankBadge, TierBadge, RoleBadge } from "@/components/Badges";
import { TESTID } from "@/lib/testIds";
import {
  Search, UserPlus, MoreVertical, KeyRound, Mail, ShieldOff,
  Trash2, X, AlertCircle, CheckCircle2, Edit3,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const RANKS = ["Nebula", "Pulsar", "Quasar", "Celestial"];
const TIERS = ["10", "30", "60"];
const ROLES = ["commander", "junior_admiral", "guardian", "admiral", "sovereign"];
const HULLS = ["Siren", "SlayMarie", "Mercy", "Vanguard", "CyberOps", "Sovereign"];

export default function RosterPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [forceResetUser, setForceResetUser] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      setToast({ kind: "error", msg: formatApiError(e) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, roleFilter]);

  const flash = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.full_name} from the roster?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      flash("ok", `${u.full_name} removed.`);
      load();
    } catch (e) {
      flash("error", formatApiError(e));
    }
  };

  const handleMagicLink = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/magic-link`);
      flash("ok", `Magic link dispatched to ${u.email}.`);
      if (data.magic_link) {
        console.log("Magic link:", data.magic_link);
      }
    } catch (e) { flash("error", formatApiError(e)); }
  };

  const handleDisableMfa = async (u) => {
    if (!window.confirm(`Force-disable MFA for ${u.full_name}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/disable-mfa`);
      flash("ok", `MFA disabled for ${u.email}.`);
      load();
    } catch (e) { flash("error", formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-armada-base">
      <ArmadaNav />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex items-end justify-between mb-8 animate-fade-up">
          <div>
            <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">ADMIRAL ROSTER</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">Fleet Personnel</h1>
            <p className="text-sm text-zinc-400 mt-2 font-light">
              Commanders & Admirals of the EIGOO Armada.
            </p>
          </div>
          <button
            data-testid={TESTID.rosterAddBtn}
            onClick={() => setOpenCreate(true)}
            className="btn-gold flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden md:inline">Recruit Commander</span>
          </button>
        </div>

        {/* Filters */}
        <div className="glass-strong rounded-xl p-4 mb-4 flex flex-col md:flex-row gap-3 animate-fade-up stagger-1">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid={TESTID.rosterSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, callsign…"
              className="armada-input pl-10"
            />
          </div>
          <select
            data-testid={TESTID.rosterRoleFilter}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="armada-input md:w-48"
          >
            <option value="">All Roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {toast && (
          <div className={`mb-4 flex items-start gap-2 p-3 rounded-md text-sm ${
            toast.kind === "error" ? "bg-red-950/40 border border-red-900/50 text-red-200" : "bg-emerald-950/40 border border-emerald-900/50 text-emerald-200"
          }`}>
            {toast.kind === "error" ? <AlertCircle className="w-4 h-4 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 mt-0.5" />}
            <span>{toast.msg}</span>
          </div>
        )}

        {/* Table */}
        <div className="glass-strong rounded-xl overflow-hidden animate-fade-up stagger-2">
          <table data-testid={TESTID.rosterTable} className="w-full">
            <thead>
              <tr className="text-left text-[10px] tracking-armada text-zinc-500 font-bold border-b border-white/5">
                <th className="px-5 py-3">Commander</th>
                <th className="px-5 py-3">Rank</th>
                <th className="px-5 py-3">Tier</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Hulls</th>
                <th className="px-5 py-3">MFA</th>
                <th className="px-5 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-500 text-sm">Loading roster…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-500 text-sm">No commanders found.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} data-testid={TESTID.rosterRow} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-sm font-bold text-white">
                        {u.full_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{u.full_name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono truncate">{u.email}</div>
                        {u.callsign && <div className="text-[10px] text-[#D4AF37] tracking-wider uppercase mt-0.5">{u.callsign}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4"><RankBadge rank={u.rank} /></td>
                  <td className="px-5 py-4"><TierBadge tier={u.revenue_tier} /></td>
                  <td className="px-5 py-4"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(u.assigned_hulls || []).slice(0, 3).map((h) => (
                        <span key={h} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300">{h}</span>
                      ))}
                      {u.assigned_hulls?.length > 3 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-500">+{u.assigned_hulls.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold ${u.mfa_enabled ? "text-emerald-400" : "text-zinc-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.mfa_enabled ? "bg-emerald-400" : "bg-zinc-600"}`} />
                      {u.mfa_enabled ? "Armored" : "Open"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded hover:bg-white/5">
                          <MoreVertical className="w-4 h-4 text-zinc-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-950 border border-white/10 text-white min-w-[180px]">
                        <DropdownMenuItem
                          data-testid={TESTID.userEdit}
                          onClick={() => setEditing(u)}
                          className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          <Edit3 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={TESTID.userSendMagic}
                          onClick={() => handleMagicLink(u)}
                          className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          <Mail className="w-4 h-4 mr-2" /> Send Magic Link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={TESTID.userForceReset}
                          onClick={() => setForceResetUser(u)}
                          className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          <KeyRound className="w-4 h-4 mr-2" /> Force Password Reset
                        </DropdownMenuItem>
                        {u.mfa_enabled && (
                          <DropdownMenuItem
                            data-testid={TESTID.userDisableMfa}
                            onClick={() => handleDisableMfa(u)}
                            className="cursor-pointer focus:bg-amber-500/10 focus:text-amber-400"
                          >
                            <ShieldOff className="w-4 h-4 mr-2" /> Disable MFA
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          data-testid={TESTID.userDelete}
                          onClick={() => handleDelete(u)}
                          className="cursor-pointer focus:bg-red-500/10 focus:text-red-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <UserFormDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={() => { setOpenCreate(false); load(); flash("ok", "Commander recruited."); }}
      />
      <UserFormDialog
        open={!!editing}
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); flash("ok", "Commander updated."); }}
      />
      <ForceResetDialog
        user={forceResetUser}
        onClose={() => setForceResetUser(null)}
        onDone={() => { setForceResetUser(null); flash("ok", "Password force-reset complete."); }}
      />
    </div>
  );
}

function UserFormDialog({ open, user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: "", full_name: "", password: "", callsign: "",
    rank: "Nebula", revenue_tier: "10", role: "commander",
    assigned_hulls: [], send_magic_link: true,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email,
        full_name: user.full_name,
        password: "",
        callsign: user.callsign || "",
        rank: user.rank,
        revenue_tier: user.revenue_tier,
        role: user.role,
        assigned_hulls: user.assigned_hulls || [],
        send_magic_link: false,
      });
    } else {
      setForm({
        email: "", full_name: "", password: "", callsign: "",
        rank: "Nebula", revenue_tier: "10", role: "commander",
        assigned_hulls: [], send_magic_link: true,
      });
    }
    setError("");
  }, [user, open]);

  const toggleHull = (h) => {
    setForm((f) => ({
      ...f,
      assigned_hulls: f.assigned_hulls.includes(h)
        ? f.assigned_hulls.filter((x) => x !== h)
        : [...f.assigned_hulls, h],
    }));
  };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (isEdit) {
        const payload = {
          full_name: form.full_name,
          callsign: form.callsign || null,
          rank: form.rank, revenue_tier: form.revenue_tier, role: form.role,
          assigned_hulls: form.assigned_hulls,
        };
        await api.patch(`/admin/users/${user.id}`, payload);
      } else {
        await api.post("/admin/users", form);
      }
      onSaved();
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border border-[#D4AF37]/20 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-black tracking-tight">
            {isEdit ? "Edit Commander" : "Recruit Commander"}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Email</label>
            <input
              data-testid={TESTID.userEmail}
              type="email" disabled={isEdit}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="armada-input mt-1 disabled:opacity-60"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Full Name</label>
              <input
                data-testid={TESTID.userName}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="armada-input mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Callsign</label>
              <input
                data-testid={TESTID.userCallsign}
                value={form.callsign}
                onChange={(e) => setForm({ ...form, callsign: e.target.value })}
                className="armada-input mt-1"
              />
            </div>
          </div>

          {!isEdit && (
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Password (optional, for password fallback)</label>
              <input
                data-testid={TESTID.userPassword}
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Leave blank for magic-link only"
                className="armada-input mt-1"
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Rank</label>
              <select data-testid={TESTID.userRank} value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} className="armada-input mt-1">
                {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Tier %</label>
              <select data-testid={TESTID.userTier} value={form.revenue_tier} onChange={(e) => setForm({ ...form, revenue_tier: e.target.value })} className="armada-input mt-1">
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Role</label>
              <select data-testid={TESTID.userRole} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="armada-input mt-1">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400 mb-2 block">Assigned Hulls</label>
            <div className="flex flex-wrap gap-2">
              {HULLS.map((h) => {
                const active = form.assigned_hulls.includes(h);
                return (
                  <button
                    key={h}
                    type="button"
                    data-testid={`${TESTID.userHullsCheckbox}-${h}`}
                    onClick={() => toggleHull(h)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold tracking-wider transition-all ${
                      active
                        ? "bg-[#D4AF37] text-black"
                        : "bg-black/40 border border-white/10 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {h}
                  </button>
                );
              })}
            </div>
          </div>

          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_magic_link}
                onChange={(e) => setForm({ ...form, send_magic_link: e.target.checked })}
                className="accent-[#D4AF37]"
              />
              Send welcome magic link
            </label>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-ghost-gold">Cancel</button>
          <button
            data-testid={TESTID.userSubmit}
            onClick={submit}
            disabled={busy || !form.email || !form.full_name}
            className="btn-gold"
          >
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Recruit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForceResetDialog({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setPw(""); setErr(""); }, [user]);

  const submit = async () => {
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/users/${user.id}/force-reset`, { new_password: pw, notify_user: true });
      onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border border-[#D4AF37]/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Force Password Reset</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-400">
          Sovereign override: Set a new password for <span className="text-white font-semibold">{user?.email}</span>.
        </p>
        {err && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5" /><span>{err}</span>
          </div>
        )}
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="New password (min 8 chars)"
          className="armada-input"
        />
        <DialogFooter>
          <button onClick={onClose} className="btn-ghost-gold">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-gold">
            {busy ? "Resetting…" : "Force Reset"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
